<template>
    <super-admin-instructions v-if="form.survey_type_id === 1" title="How to use a 60-Second Temperature Check">
        <template v-slot:instructions>
            <div class="card-instructions">
                <div class="flex flex-col lg:flex-row pt-10">
                    <div class="text-lg leading-5 w-2/3 pr-6 border-r-4 border-dark">
                        <p class="mb-4">Our 60-second Temperature Check has been designed to act as a short, sharp temperature check of your organisational health. </p>
                        <p class="mb-4">Taking no more than a minute to complete, this diagnostic gives you an indication of your PROPEL score, without draining your staff time.</p>
                        <p class="mb-4">Use this if you don't need in-depth analytics, but want a gage of where you're at.</p>
                        <p>The whole purpose of the 60-second staff survey is that it's light touch - as such, you won't be able to view in depth analysis like you can when running a 30-minute Health Screening. Instead you'll get a score for each PROPEL segment, rather than each question.</p>
                    </div>
                    <div class="flex-1 pl-6 text-2xl leading-6">
                        <h3 class="font-bold mb-4">Pricing</h3>
                        <p v-for="(unitPrice, unitPriceIndex) in unitPrices"
                           :key="'unitPrice-' + unitPriceIndex"
                           class="mb-4">
                            {{
                                unitPriceIndex === unitPrices.length - 1
                                    ? unitPrice.min_respondents + '+'
                                    : unitPrice.min_respondents + ' - '
                                    + (unitPrices[unitPriceIndex + 1].min_respondents - 1)
                            }}
                            : &pound;{{ (unitPrice.value / 100).toFixed(2) }}
                        </p>
                        <!--<p class="mb-4">FREE for up to 25 invites</p>
                        <p class="mb-4">£1 per invitee after that</p>
                        <p class="mb-4">Just £75 for a team of 100.</p>-->
                    </div>
                </div>
            </div>
        </template>
    </super-admin-instructions>

    <super-admin-instructions v-if="form.survey_type_id === 2" title="How to use a 30-minute Health Screening">
        <template v-slot:instructions>
            <div class="card-instructions">
                <div class="flex flex-col lg:flex-row pt-10">
                    <div class="text-lg leading-5 w-2/3 pr-6 border-r-4 border-dark">
                        <p class="mb-4">Our 60-second Temperature Check has been designed to act as a short, sharp temperature check of your organisational health. </p>
                        <p class="mb-4">Taking no more than a minute to complete, this diagnostic gives you an indication of your PROPEL score, without draining your staff time.</p>
                        <p class="mb-4">Use this if you don't need in-depth analytics, but want a gage of where you're at.</p>
                        <p>The whole purpose of the 60-second staff survey is that it's light touch - as such, you won't be able to view in depth analysis like you can when running a 30-minute Health Screening. Instead you'll get a score for each PROPEL segment, rather than each question.</p>
                    </div>
                    <div class="flex-1 pl-6 text-2xl leading-6">
                        <h3 class="font-bold mb-4">Pricing</h3>
                        <p v-for="(unitPrice, unitPriceIndex) in unitPrices"
                           :key="'unitPrice-' + unitPriceIndex"
                           class="mb-4">
                            {{
                                unitPriceIndex === unitPrices.length - 1
                                    ? unitPrice.min_respondents + '+'
                                    : unitPrice.min_respondents + ' - '
                                    + (unitPrices[unitPriceIndex + 1].min_respondents - 1)
                            }}
                            : &pound;{{ (unitPrice.value / 100).toFixed(2) }}
                        </p>
                        <!--<p class="mb-4">FREE for up to 25 invites</p>
                        <p class="mb-4">£1 per invitee after that</p>
                        <p class="mb-4">Just £75 for a team of 100.</p>-->
                    </div>
                </div>
            </div>
        </template>
    </super-admin-instructions>

    <form @submit.prevent="submit()">
        <div class="p-6 mb-6 shadow-md rounded-md">
            <div class="text-2xl leading-6 flex justify-between items-center mb-4">
                <h3 class="font-bold">{{ stepTitle }}</h3>
                <p>Step {{ step }} of 5</p>
            </div>
            <div class="divide-y divide-dark">
                <div class="mb-6">
                    <h4 class="text-xl leading-6 font-bold mb-4">Survey info</h4>
                    <div class="grid gap-6 md:grid-cols-3">
                        <div>
                            <input type="file" accept="image/*" class="hidden" ref="photo" @change="uploadLogoFile">
                            <label class="block text-sm mb-2">
                                <span class="block text-lg font-semibold leading-6 mb-2.5">Logo</span>
                            </label><!-- Current Profile Photo -->
                            <div class="mt-2" v-if="!form.logo">
                                <img
                                    src="/images/logo-placeholder.jpg"
                                    alt="Placeholder logo"
                                    class="h-20 max-w-md">
                            </div>
                            <div class="mt-2" v-else-if="form.logo">
                                <img
                                    :src="form.logo_url"
                                    alt="Placeholder logo"
                                    class="h-20 max-w-md">
                            </div>
                            <jet-button type="button" class="mt-2"
                                        v-if="form.logo === defaultLogo.file"
                                        @click.prevent="selectNewLogo"
                                        :has-icon="true" :bg-dark="true"
                            >
                                Add your organisations logo
                            </jet-button>
                            <jet-button type="button" class="ml-2"
                                        @click.prevent="resetLogo"
                                        :bg-dark="true"
                                        v-if="form.logo && form.logo !== defaultLogo.file">
                                Reset Logo
                            </jet-button>
                            <jet-input-error :message="form.errors['defaults.logo']"/>
                        </div>
                        <div class="col-span-2">
                            <label class="block text-sm mb-6">
                                <span class="block text-lg font-semibold leading-6 mb-2.5">Title</span>
                                <jet-input type="text" v-model="form.name"
                                           class="block w-full"
                                />
                            </label>
                            <div class="block text-sm">
                                <div class="flex items-center flex-wrap mb-2.5">
                                    <span class="text-lg font-semibold leading-6">Intro Text</span>
                                    <button @click.prevent="revertToDefaultIntroText"
                                            class="flex items-center justify-between ml-2 px-1 py-1 text-xs font-medium leading-5 transition-colors duration-150 border border-gray-300 bg-light dark:border-gray-500 dark:bg-gray-600 dark:text-gray-200 border-0 focus:outline-none focus:shadow-none rounded-lg"
                                            aria-label="Revert to default" title="Revert to default">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                    </button>
                                    <span class="block w-full">Customise the introduction that people will see when they access your survey.</span>
                                </div>

                                <ckeditor :editor="editor"
                                          v-model="form.intro_text"
                                          class="block w-full mt-1 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-textarea focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:focus:shadow-outline-gray"
                                          :config="editorConfig"></ckeditor>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="pt-6">
                    <h4 class="text-xl leading-6 font-bold mb-4">Custom fields</h4>
                    <div class="grid gap-6 md:grid-cols-3">
                        <div>
                            <h5 class="block text-lg font-semibold leading-6 mb-2.5">Add and customise these optional fields</h5>
                            <p class="mb-2.5 leading-6">Customise the additional fields that people will see when they access this survey.</p>
                            <div v-for="(defaultProfileField, defProfFiledIndex) in defaultSurveyProfileFields"
                                 :key="'defaultProfileField-' + defProfFiledIndex"
                                 class="flex flex-wrap text-sm mb-2.5">
                                <label class="flex items-center">
                                    <jet-checkbox
                                        v-model:checked="form.profile_fields"
                                        :value="defaultProfileField">
                                        {{ defaultProfileField.name }}
                                    </jet-checkbox>
                                </label>
                                <div v-if="isDefaultProfileFieldSelected(defaultProfileField.name)"
                                     class="customize-options w-full p-2 mt-2">
                                    <div class="" :ref="(el) => saveDefProfileValueContainerRef(el, defProfFiledIndex)">
                                        <div
                                            v-for="(profileFieldValue, valueIndex) in defaultProfileField.values"
                                            :key="'defaultProfileFieldValue-' + defProfFiledIndex + '-' + valueIndex"
                                            class="block text-sm field-value">
                                            <div class="relative">
                                                <jet-input type="text"
                                                           :required="true"
                                                           v-model="defaultProfileField.values[valueIndex].value"
                                                           class="block w-full pr-10 mb-2.5"
                                                />
                                                <button
                                                    v-if="defaultProfileField.values.length > 1"
                                                    @click.prevent="removeProfileFieldValue(defaultProfileField, valueIndex, true)"
                                                    class="absolute inset-y-0 right-0 px-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-dark border border-transparent rounded-r-md">
                                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="white">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="flex align-end">
                                            <jet-button
                                                @click.prevent="newProfileFieldValue(defaultProfileField, true)"
                                                :has-icon="true"
                                                :bg-dark="true"
                                            >
                                                New option
                                            </jet-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-span-2">
                            <h5 class="block text-lg font-semibold leading-6 mb-2.5">Add and customise these optional fields</h5>
                            <template v-for="(profileField, profileFieldIndex) in form.profile_fields"
                                      :key="'customProfileField-' + profileFieldIndex">
                                <div v-if="!defaultProfileFields.includes(profileField.name)" class="custom-field mb-6">
                                    <div class="block">
                                        <span class="block text-lg font-semibold leading-6 mb-2.5">Field Name</span>
                                        <div class="relative">
                                            <jet-input type="text"
                                                       :required="true"
                                                       v-model="profileField.name"
                                                       class="block w-full pr-20"/>
                                            <button
                                                @click.prevent="removeSurveyProfileField(profileFieldIndex)"
                                                class="absolute inset-y-0 right-0 px-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-dark border border-transparent rounded-r-md">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                                     fill="none" viewBox="0 0 24 24" stroke="white">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                          stroke-width="2"
                                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="options-field px-6 py-3">
                                        <div class="mb-2 grid gap-3 md:grid-cols-2 items-center" :ref="(el) => saveProfileValueContainerRef(el, profileFieldIndex)">
                                            <div  v-for="(profileFieldValue, valueIndex) in profileField.values"
                                                  :key="'customProfileFieldValue-' + profileFieldIndex + '-' + valueIndex"
                                                  class="block text-sm field-value">
                                                <div class="relative">
                                                    <jet-input type="text"
                                                               :required="true"
                                                               v-model="profileField.values[valueIndex].value"
                                                               class="block w-full pr-10"/>
                                                    <button
                                                        v-if="profileField.values.length > 1"
                                                        @click.prevent="removeProfileFieldValue(profileField, valueIndex, false); $forceUpdate()"
                                                        class="absolute inset-y-0 right-0 px-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-dark border border-transparent rounded-r-md">
                                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                                             fill="none" viewBox="0 0 24 24" stroke="white">
                                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                                  stroke-width="2"
                                                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <jet-button
                                                    @click.prevent="newProfileFieldValue(profileField, false); $forceUpdate()"
                                                    :has-icon="true"
                                                    :bg-dark="true"
                                                >
                                                    New option
                                                </jet-button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <jet-button
                                @click="newSurveyProfileField()"
                                :has-icon="true"
                                :bg-dark="true"
                            >
                                New field
                            </jet-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex justify-between">
            <jet-button @click="form.next_step = 3" :disabled="form.processing"
                        class="ml-auto"
                        :bg-dark="true"
            >
                Next step
                <svg class="ml-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/>
                </svg>
            </jet-button>
        </div>
    </form>
</template>

<script>
import JetButton from "@/Jetstream/Button";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetInputError from "@/Jetstream/InputError";
import JetInput from "@/Jetstream/Input";
import JetCheckbox from "@/Jetstream/Checkbox";
import JetTextarea from "@/Jetstream/Textarea";
import SuperAdminInstructions from "@/Components/SuperAdminDashboard/SuperAdminInstructions";
import {useForm} from "@inertiajs/inertia-vue3";
import {nextTick, onMounted, ref} from "vue";
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CKEditor from '@ckeditor/ckeditor5-vue';

export default {
    name: "SurveyEditorStepTwo",
    components: {
        JetButton,
        JetSecondaryButton,
        JetInputError,
        JetInput,
        JetCheckbox,
        JetTextarea,
        SuperAdminInstructions,
        ckeditor: CKEditor.component
    },
    emits: ['next-step'],
    props: {
        surveyModel: {
            type: Object,
            required: true,
        },
        defaultLogo: {
            type: Object,
            required: true,
        },
        defaultProfileFields: {
            type: Array,
            required: true,
        },
        defaultIntroText: {
            type: [String, null],
            required: true,
        },
        unitPrices: {
            type: Object,
            default: [{
                min_respondents: 0,
                value: 0,
            }],
        },
        step: {
            type: Number,
            required: true
        },
        stepTitle: {
            type: String,
            required: true
        }
    },
    setup(props, {emit}) {

        const form = useForm({
            step: 2,
            next_step: 3,
            survey_type_id: props.surveyModel.survey_type_id,
            name: props.surveyModel.name,
            logo: props.surveyModel.logo,
            logo_url: props.surveyModel.logo_url,
            logo_file: null,
            intro_text: props.surveyModel.intro_text || '',
            profile_fields: [...props.surveyModel.profile_fields.map((profileField) => {
                return {id: profileField.id, name: profileField.name, values: profileField.values};
            })],
        });

        const photo = ref(null);

        const selectNewLogo = () => {
            photo.value.click();
        }

        const resetLogo = () => {
            form.logo = props.defaultLogo.file;
            form.logo_url = props.defaultLogo.url;
            form.next_step = 2;
            submit();
        }

        const submit = () => {
            form.logo_file = null;
            form.put(
                route('tenant.survey.update', {tenant: props.surveyModel.tenant_id, survey: props.surveyModel.id}),
                {
                    onSuccess: () => {
                        if (form.next_step === 3) {
                            emit('next-step');
                        }
                        clearPhotoFileInput();
                    }
                }
            );
        }

        const uploadLogoFile = () => {
            form.logo_file = photo.value.files[0];
            form.post(
                route('tenant.survey.upload-file', {tenant: props.surveyModel.tenant_id, survey: props.surveyModel.id}),
                {
                    onSuccess: () => {
                        form.logo = props.surveyModel.logo;
                        form.logo_url = props.surveyModel.logo_url;
                        clearPhotoFileInput();
                    }
                }
            );
        }

        const defaultSurveyProfileFields = ref([]);
        defaultSurveyProfileFields.value = props.defaultProfileFields.map((profileFieldName) => {
            let surveyProfileField = form.profile_fields.filter(
                (formProfileField) => formProfileField.name === profileFieldName
            );
            if (surveyProfileField.length) {
                return {
                    id: surveyProfileField[0].id,
                    name: surveyProfileField[0].name,
                    values: surveyProfileField[0].values,
                }
            }
            return {
                name: profileFieldName,
                values: [{
                    value: null,
                }],
            }
        });

        const isDefaultProfileFieldSelected = (profileFieldName) => {
            return !!form.profile_fields.filter((profileField) => profileField.name === profileFieldName).length;
        };

        const removeProfileFieldValue = (profileField, valueIndex, isDefaultProfileField) => {
            let array;
            if (isDefaultProfileField) {
                array = defaultSurveyProfileFields.value;
            } else {
                array = form.profile_fields;
            }

            const profileFieldIndex = array.findIndex(
                (formProfileField) => formProfileField.name === profileField.name
            );
            if (profileFieldIndex !== -1 && array[profileFieldIndex].values.length > 1) {
                array[profileFieldIndex].values.splice(valueIndex, 1);
            }
        }

        const newProfileFieldValue = (profileField, isDefaultProfileField) => {
            let array;
            let refsArray;
            if (isDefaultProfileField) {
                array = defaultSurveyProfileFields.value;
                refsArray = defaultProfFieldValuesContainerRefs.value;
            } else {
                array = form.profile_fields;
                refsArray = profFieldValuesContainerRefs.value;
            }

            const profileFieldIndex = array.findIndex(
                (formProfileField) => formProfileField.name === profileField.name
            );

            if (profileFieldIndex !== -1) {
                array[profileFieldIndex].values.push({value: null});
                nextTick(() => {
                    const valueNodes = refsArray[profileFieldIndex].querySelectorAll('.field-value');
                    valueNodes[valueNodes.length - 1].querySelector('input')?.focus();
                });
            }
        }

        const removeSurveyProfileField = (index) => {
            form.profile_fields.splice(index, 1);
        };

        const newSurveyProfileField = () => {
            form.profile_fields.push({
                name: null,
                values: [{
                    value: null
                }],
            });
        };

        const revertToDefaultIntroText = () => {
            form.intro_text = props.defaultIntroText || '';
        };

        const clearPhotoFileInput = () => {
            if (photo.value?.value){
                photo.value.value = null;
            }
        };

        const editor = ClassicEditor;
        const editorConfig = {
            removePlugins: ['imageUpload', 'mediaEmbed'],
            link: {
                addTargetToExternalLinks: true,
                defaultProtocol: 'http://',
            },
            toolbar: {
                removeItems: ['uploadImage', 'mediaEmbed']
            },
        };

        const defaultProfFieldValuesContainerRefs = ref([]);
        const saveDefProfileValueContainerRef = (el, index) => {
            if (el) {
                defaultProfFieldValuesContainerRefs.value[index] = el;
            }
        };

        const profFieldValuesContainerRefs = ref([]);
        const saveProfileValueContainerRef = (el, index) => {
            if (el) {
                profFieldValuesContainerRefs.value[index] = el;
            }
        };

        return {
            form,
            photo,
            selectNewLogo,
            resetLogo,
            submit,
            uploadLogoFile,
            defaultSurveyProfileFields,
            isDefaultProfileFieldSelected,
            removeProfileFieldValue,
            newProfileFieldValue,
            removeSurveyProfileField,
            newSurveyProfileField,
            revertToDefaultIntroText,
            editor,
            editorConfig,
            saveDefProfileValueContainerRef,
            saveProfileValueContainerRef,
        }
    }
}
</script>

<style scoped>

</style>
